interface CashFlow {
  amount: number;
  date: string | Date;
}

interface XirrConfig {
  initialGuess?: number;
  tolerance?: number;
  maxIterations?: number;
  daysInYear?: number;
  useBracketing?: boolean;
}

const MILLIS_PER_DAY = 1000 * 60 * 60 * 24;

export function calculateXirr(cashFlows: CashFlow[], config: XirrConfig = {}): number {
  const {
    initialGuess = 0.1,
    tolerance = 1e-10,
    maxIterations = 100,
    daysInYear = 365,
    useBracketing = true,
  } = config;

  if (cashFlows.length < 2) {
    throw new Error('XIRR requires at least two cash flows.');
  }

  // Normalize and validate cash flows
  const normalizedCashFlows = cashFlows
    .map(flow => ({
      amount: flow.amount,
      date: new Date(flow.date),
    }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  // Combine cash flows that occur on the same date
  const combinedCashFlows: Array<{ amount: number; date: Date }> = [];

  for (const flow of normalizedCashFlows) {
    const existing = combinedCashFlows.find(
      cf => cf.date.getTime() === flow.date.getTime()
    );
    if (existing) {
      existing.amount += flow.amount;
    } else {
      combinedCashFlows.push({ ...flow });
    }
  }

  const finalCashFlows = combinedCashFlows;

  const hasPositiveCashFlow = finalCashFlows.some(flow => flow.amount > 0);
  const hasNegativeCashFlow = finalCashFlows.some(flow => flow.amount < 0);

  if (!hasPositiveCashFlow || !hasNegativeCashFlow) {
    throw new Error('XIRR requires at least one positive and one negative cash flow.');
  }

  const cashFlowScale = finalCashFlows.reduce((sum, flow) => sum + Math.abs(flow.amount), 0);
  const npvTolerance = tolerance * Math.max(1, cashFlowScale);

  const baseCashFlowDate = finalCashFlows[0].date.getTime();

  // Convert each flow into years offset from base date
  const cashFlowsWithTime = finalCashFlows.map(flow => {
    const daysDiff = (flow.date.getTime() - baseCashFlowDate) / MILLIS_PER_DAY;
    const yearFraction = daysDiff / daysInYear;

    return { amount: flow.amount, yearFraction };
  });

  // Define the XIRR formula and its derivative
  function netPresentValue(rate: number): number {
    const base = 1 + rate;

    if (base <= 0) return Infinity;

    return cashFlowsWithTime.reduce(
      (sum, flow) => sum + flow.amount / Math.pow(base, flow.yearFraction),
      0,
    );
  }

  function netPresentValueDerivative(rate: number): number {
    const base = 1 + rate;

    if (base <= 0) return -Infinity;

    return cashFlowsWithTime.reduce(
      (sum, flow) =>
        sum - (flow.yearFraction * flow.amount) / Math.pow(base, flow.yearFraction + 1),
      0,
    );
  }

  // Find bracket [low, high] where NPV changes sign
  function findBracketingRange(): [number, number] | null {
    const candidateRates = [
      -0.9999, -0.75, -0.5, -0.25, -0.1, -0.05, 0, 0.01, 0.05, 0.1, 0.25, 0.5, 0.75, 1, 1.5, 2, 3,
      5, 10,
    ];

    let previousRate = candidateRates[0];
    let previousValue = netPresentValue(previousRate);

    for (const rate of candidateRates.slice(1)) {
      const currentValue = netPresentValue(rate);

      if (previousValue * currentValue <= 0) {
        return [previousRate, rate];
      }

      previousRate = rate;
      previousValue = currentValue;
    }
    return null;
  }

  function performBisection(lowerRate: number, upperRate: number): number {
    let lowerValue = netPresentValue(lowerRate);
    let upperValue = netPresentValue(upperRate);

    if (lowerValue * upperValue > 0) {
      throw new Error('Invalid bracket for XIRR — no sign change detected.');
    }

    for (let iteration = 0; iteration < 200; iteration++) {
      const midRate = (lowerRate + upperRate) / 2;
      const midValue = netPresentValue(midRate);

      if (Math.abs(midValue) < npvTolerance || Math.abs(upperRate - lowerRate) < tolerance) {
        return midRate;
      }

      if (lowerValue * midValue < 0) {
        upperRate = midRate;
        upperValue = midValue;
      } else {
        lowerRate = midRate;
        lowerValue = midValue;
      }
    }

    return (lowerRate + upperRate) / 2;
  }

  // Newton-Raphson root-finding
  const bracket = useBracketing ? findBracketingRange() : null;

  function clampToBracket(rate: number) {
    return bracket ? Math.min(Math.max(rate, bracket[0]), bracket[1]) : rate;
  }

  let currentRate = clampToBracket(initialGuess);
  let currentValue = netPresentValue(currentRate);

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    const derivative = netPresentValueDerivative(currentRate);

    if (Math.abs(derivative) < 1e-16) break;

    const nextRate = clampToBracket(currentRate - currentValue / derivative);
    const nextValue = netPresentValue(nextRate);

    if (Math.abs(nextValue) < npvTolerance) {
      return nextRate;
    }

    if (Math.abs(nextRate - currentRate) < tolerance) break;

    currentRate = nextRate;
    currentValue = nextValue;
  }

  // Fallback to bisection if Newton fails
  if (bracket) {
    return performBisection(bracket[0], bracket[1]);
  }

  throw new Error('XIRR could not converge. Try adjusting initialGuess or review cash flows.');
}
